import React from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { CommitDetailsGraph } from "./CommitDetailsGraph";
import { COMMIT, FUNCTIONS, MetricCPU, MetricULS, MetricsSubTypes } from "../utils/constants";
import { groupProcesses, renderGroupedProcessOptions } from "../utils/processGrouping.tsx";
import '../styles/FarmCards.css';
import MetaDataCard from "./MetaDataCard.tsx";
import { useConfig } from "../configContext";

const supportedRepresentations = new Set([
    'standard',
    'profileUsage',
    'normalizedProfileUsage',
]);

function normalizeRepresentation(representation) {
    const legacyAliases = {
        sampleCount: 'profileUsage',
        normalizedSampleCount: 'normalizedProfileUsage',
    };
    const normalizedRepresentation = legacyAliases[representation] ?? representation;

    return supportedRepresentations.has(normalizedRepresentation)
        ? normalizedRepresentation
        : 'standard';
}

/**
 * Get list of available metrics for a process
 * @param {Object} commitDetails - commit details with metrics
 * @param {number} processId - process ID to check
 * @param {string} env - environment name
 * @returns {number[]} array of available metric type IDs
 */
function getAvailableMetrics(commitDetails, processId, env) {
    if (!commitDetails?.commitMetrics) return [];

    const availableMetrics = [];

    for (const metric of commitDetails.commitMetrics) {
        // Check if this metric has data for the given process and env
        const hasFarmMetric = metric.farmMetrics?.some(fm =>
            fm.process === processId && fm.env === env
        );

        if (hasFarmMetric) {
            availableMetrics.push(metric.metricType);
        }
    }

    return availableMetrics;
}

export default function FarmCards({ serviceName, commitDetails }) {
    const config = useConfig();
    const service = config.services.find(s => s.name === serviceName);
    const serviceId = service.id;
    const serviceEnvironments = service?.environments ? Object.values(service.environments) : [];
    const processes = service?.processes ? Object.values(service.processes) : [];
    const groupedProcesses = groupProcesses(processes);
    const hasNonRealProcesses = processes.some(p => p.id >= 1000);
    const regression = commitDetails.regressions?.find(r => r.isRegression && r.source === 2);

    const location = useLocation();
    const navigate = useNavigate();
    let navState = location.state;
    if (navState == null) {
        const qs = new URLSearchParams(location.search);
        navState = {
            env: qs.get('env') != null ? Number(qs.get('env')) : (regression ? regression.environmentId : service?.defaultConfig?.environmentId),
            process: qs.get('process') != null ? Number(qs.get('process')) : (regression ? regression.processId : service?.defaultConfig?.processId),
            metric: qs.get('metric') != null ? Number(qs.get('metric')) : (regression ? regression.metricType : service?.defaultConfig?.metricId),
            graph: qs.get('graph') != null ? qs.get('graph') : COMMIT,
            profileDate: qs.get('profiledate'),
            representation: normalizeRepresentation(qs.get('representation')),
        };
    } else {
        navState = {
            ...navState,
            representation: normalizeRepresentation(navState.representation),
        };
    }

    const availableDataMetrics = getAvailableMetrics(
        commitDetails,
        navState.process,
        navState.env,
    );
    if (!availableDataMetrics.includes(navState.metric)
        && availableDataMetrics.length > 0) {
        const fallbackMetric = availableDataMetrics[0];
        navState = {
            ...navState,
            metric: fallbackMetric,
            graph: COMMIT,
            representation: fallbackMetric === MetricCPU
                ? navState.representation
                : 'standard',
        };
    }

    // Get metrics for the selected process
    const selectedProcess = processes.find(p => p.id === navState.process);
    const supportsProcessRepresentations =
        selectedProcess?.enableProcessObservationCollection === true
        && navState.metric === MetricCPU;
    if (!supportsProcessRepresentations) {
        navState = {
            ...navState,
            representation: 'standard',
        };
    }
    const availableMetrics = selectedProcess ? Object.values(selectedProcess.metrics) : [];
    if (navState.metric === MetricULS)
        navState = { ...navState, graph: COMMIT, profileDate: null, representation: 'standard' };

    const onEnvSelect = (event) => {
        nav({ env: +event.target.value });
    };

    const onProcessSelect = (event) => {
        const newProcessId = +event.target.value;
        const newProcess = processes.find(process => process.id === newProcessId);

        // Get list of metrics available for this process
        const availableMetrics = getAvailableMetrics(commitDetails, newProcessId, navState.env);

        // Check if current metric is available for new process
        const currentMetricAvailable = availableMetrics.includes(navState.metric);

        if (!currentMetricAvailable && availableMetrics.length > 0) {
            // Current metric not available, auto-select first available metric
            const metric = availableMetrics[0];
            nav({
                process: newProcessId,
                metric,
                representation:
                    newProcess?.enableProcessObservationCollection === true
                    && metric === MetricCPU
                        ? navState.representation
                        : 'standard',
            });
        } else {
            // Current metric is valid (or no metrics available), just update process
            nav({
                process: newProcessId,
                representation:
                    newProcess?.enableProcessObservationCollection === true
                    && navState.metric === MetricCPU
                        ? navState.representation
                        : 'standard',
            });
        }
    };

    const onMetricSelect = (event) => {
        const newMetric = +event.target.value;
        const representation =
            selectedProcess?.enableProcessObservationCollection === true
            && newMetric === MetricCPU
            ? navState.representation
            : 'standard';

        if (newMetric === MetricULS || (MetricsSubTypes.includes(newMetric) && navState.graph === FUNCTIONS)) {
            nav({ metric: newMetric, graph: COMMIT, representation });
        } else {
            nav({ metric: newMetric, representation });
        }
    };

    const onGraphSelect = (event) => {
        nav({ graph: event.target.value });
    };

    const onRepresentationSelect = (event) => {
        nav({ representation: event.target.value });
    };

    const nav = (update) => {
        const targetState = { ...navState, ...update };
        const qs = new URLSearchParams();
        qs.set('env', targetState.env);
        qs.set('process', targetState.process);
        qs.set('metric', targetState.metric);
        if (targetState.graph !== COMMIT) {
            qs.set('graph', targetState.graph);
        }
        if (targetState.profileDate != null) {
            qs.set('profiledate', targetState.profileDate);
        }
        const targetProcess = processes.find(
            process => process.id === targetState.process);
        if (targetProcess?.enableProcessObservationCollection === true
            && targetState.metric === MetricCPU
            && targetState.representation !== 'standard') {
            qs.set('representation', targetState.representation);
        }

        const url = location.pathname + '?' + qs.toString();
        navigate(url, { state: targetState })
    }

    const representation = supportsProcessRepresentations
        ? navState.representation
        : 'standard';
    const metricWithSubtypes = commitDetails.commitMetrics.find(m => m.metricType === navState.metric && MetricsSubTypes.includes(m.metricType));
    return (
        <>
            <MetaDataCard service={service} commitDetails={commitDetails} env={navState.env} processId={navState.process} metric={navState.metric} />
            <div className="farmCard">
                <div className="flex-container">
                    <div className="label-select-container">
                        <label className="label-style">Environment:</label>
                        <select value={navState.env} onChange={onEnvSelect} className="select-style">
                            {serviceEnvironments.map(env => (
                                <option key={env.id} value={env.id}>{env.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="label-select-container">
                        <label className="label-style">{hasNonRealProcesses ? 'Process or api:' : 'Process:'}</label>
                        <select value={navState.process} onChange={onProcessSelect} className="select-style">
                            {renderGroupedProcessOptions(groupedProcesses)}
                        </select>
                    </div>
                    <div className="label-select-container">
                        <label className="label-style">Metric:</label>
                        <select value={navState.metric} onChange={onMetricSelect} className="select-style">
                            {availableMetrics.map(metrics => (
                                <option key={metrics.id} value={metrics.id}>{metrics.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="label-select-container">
                        <label className="label-style">Graph:</label>
                        <select value={navState.graph} onChange={onGraphSelect} className="select-style">
                            <option value={COMMIT}>Overall</option>
                            {navState.metric === MetricULS ? null : !MetricsSubTypes.includes(navState.metric) ? (
                                <option value={FUNCTIONS}>Functions</option>
                            ) : metricWithSubtypes?.farmMetrics
                                ?.find(farm => farm.env === navState.env && farm.process === navState.process)
                                ?.metricSubTypes
                                ?.filter(obj => obj.metricSubType !== "All")
                                ?.map(obj => (
                                    <option key={obj.metricSubType} value={obj.metricSubType} title={obj.metricSubType}>
                                        {obj.metricSubType}
                                    </option>
                                ))}
                        </select>
                    </div>
                    {supportsProcessRepresentations && (
                        <div className="label-select-container">
                            <label className="label-style">Representation:</label>
                            <select
                                aria-label="Representation"
                                value={navState.representation}
                                onChange={onRepresentationSelect}
                                className="select-style"
                            >
                                <option value="standard">Standard</option>
                                <option value="profileUsage">Profile usage</option>
                                <option value="normalizedProfileUsage">Normalized profile usage</option>
                            </select>
                        </div>
                    )}
                </div>
                <CommitDetailsGraph
                    serviceId={serviceId}
                    env={navState.env}
                    type={navState.graph}
                    commitDetails={commitDetails}
                    process={navState.process}
                    metric={navState.metric}
                    profileDate={navState.profileDate}
                    representation={representation}
                    nav={nav}
                />
            </div>
        </>
    );
}
